import localforage from 'localforage';

/**
 * Configuración de stores de IndexedDB
 */

// Store para datos en cache
export const cacheStore = localforage.createInstance({
  name: 'merino-crm',
  storeName: 'cache',
  description: 'Cache de datos del servidor',
});

// Store para operaciones pendientes (offline queue)
export const queueStore = localforage.createInstance({
  name: 'merino-crm',
  storeName: 'offline-queue',
  description: 'Cola de operaciones pendientes offline',
});

// Store para metadata
export const metaStore = localforage.createInstance({
  name: 'merino-crm',
  storeName: 'metadata',
  description: 'Metadatos de sincronización',
});

/**
 * Tipos para la cola de operaciones
 */
export interface QueuedOperation {
  id: string;
  type: 'create' | 'update' | 'delete';
  entity: string; // 'citas', 'clientes', etc.
  data: any;
  timestamp: number;
  retries: number;
}

/**
 * Cache helpers
 */
export const cacheHelpers = {
  /**
   * Guardar datos en cache
   */
  async set<T>(key: string, data: T, ttl?: number): Promise<void> {
    const cacheEntry = {
      data,
      timestamp: Date.now(),
      ttl,
    };
    await cacheStore.setItem(key, cacheEntry);
  },

  /**
   * Obtener datos del cache
   */
  async get<T>(key: string): Promise<T | null> {
    const cacheEntry = await cacheStore.getItem<{
      data: T;
      timestamp: number;
      ttl?: number;
    }>(key);

    if (!cacheEntry) return null;

    // Verificar TTL
    if (cacheEntry.ttl) {
      const isExpired = Date.now() - cacheEntry.timestamp > cacheEntry.ttl;
      if (isExpired) {
        await cacheStore.removeItem(key);
        return null;
      }
    }

    return cacheEntry.data;
  },

  /**
   * Limpiar cache expirado
   */
  async cleanExpired(): Promise<void> {
    const keys = await cacheStore.keys();
    
    for (const key of keys) {
      const entry = await cacheStore.getItem<{
        timestamp: number;
        ttl?: number;
      }>(key);
      
      if (entry?.ttl) {
        const isExpired = Date.now() - entry.timestamp > entry.ttl;
        if (isExpired) {
          await cacheStore.removeItem(key);
        }
      }
    }
  },

  /**
   * Limpiar todo el cache
   */
  async clear(): Promise<void> {
    await cacheStore.clear();
  },
};

/**
 * Queue helpers
 */
export const queueHelpers = {
  /**
   * Agregar operación a la cola
   */
  async add(operation: Omit<QueuedOperation, 'id' | 'timestamp' | 'retries'>): Promise<string> {
    const id = `${operation.entity}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const queuedOp: QueuedOperation = {
      ...operation,
      id,
      timestamp: Date.now(),
      retries: 0,
    };
    
    await queueStore.setItem(id, queuedOp);
    return id;
  },

  /**
   * Obtener todas las operaciones pendientes
   */
  async getAll(): Promise<QueuedOperation[]> {
    const keys = await queueStore.keys();
    const operations: QueuedOperation[] = [];
    
    for (const key of keys) {
      const op = await queueStore.getItem<QueuedOperation>(key);
      if (op) operations.push(op);
    }
    
    // Ordenar por timestamp (más antiguas primero)
    return operations.sort((a, b) => a.timestamp - b.timestamp);
  },

  /**
   * Eliminar operación de la cola
   */
  async remove(id: string): Promise<void> {
    await queueStore.removeItem(id);
  },

  /**
   * Incrementar reintentos de una operación
   */
  async incrementRetries(id: string): Promise<void> {
    const op = await queueStore.getItem<QueuedOperation>(id);
    if (op) {
      op.retries += 1;
      await queueStore.setItem(id, op);
    }
  },

  /**
   * Limpiar toda la cola
   */
  async clear(): Promise<void> {
    await queueStore.clear();
  },
};

/**
 * Metadata helpers
 */
export const metaHelpers = {
  /**
   * Guardar último timestamp de sync
   */
  async setLastSync(entity: string, timestamp: number): Promise<void> {
    await metaStore.setItem(`lastSync:${entity}`, timestamp);
  },

  /**
   * Obtener último timestamp de sync
   */
  async getLastSync(entity: string): Promise<number | null> {
    return await metaStore.getItem(`lastSync:${entity}`);
  },

  /**
   * Verificar si hay operaciones pendientes
   */
  async hasPendingOperations(): Promise<boolean> {
    const keys = await queueStore.keys();
    return keys.length > 0;
  },
};

/**
 * API unificada de offline storage (compatible con sync-engine)
 */
export const offlineStorage = {
  // Sync queue operations
  async getSyncQueue(): Promise<QueuedOperation[]> {
    return await queueHelpers.getAll();
  },

  async addToSyncQueue(operation: Omit<QueuedOperation, 'id' | 'timestamp' | 'retries'>): Promise<string> {
    return await queueHelpers.add(operation);
  },

  async removeFromSyncQueue(id: string): Promise<void> {
    return await queueHelpers.remove(id);
  },

  async clearSyncQueue(): Promise<void> {
    return await queueHelpers.clear();
  },

  // Inspection operations
  async saveInspection(id: string, data: any): Promise<void> {
    await cacheHelpers.set(`inspection:${id}`, data);
  },

  async getInspection(id: string): Promise<any> {
    return await cacheHelpers.get(`inspection:${id}`);
  },

  async removeInspection(id: string): Promise<void> {
    await cacheStore.removeItem(`inspection:${id}`);
  },

  // Photo operations
  async savePhoto(id: string, photoData: { dataUrl: string; metadata?: any }): Promise<void> {
    await cacheHelpers.set(`photo:${id}`, photoData);
  },

  async getPhoto(id: string): Promise<{ dataUrl: string; metadata?: any } | null> {
    return await cacheHelpers.get(`photo:${id}`);
  },

  async removePhoto(id: string): Promise<void> {
    await cacheStore.removeItem(`photo:${id}`);
  },

  // Cache operations
  cache: cacheHelpers,
  queue: queueHelpers,
  meta: metaHelpers,
};
